import { StylesConfig as ReactSelectStyles, Theme as RSTheme } from 'react-select';
import { Theme } from '../../../../theme/theme';

export type FormVariant = 'success' | 'warning' | 'error';

export type SelectVariant = 'simple' | 'toggle' | 'checkbox' | 'icon';

interface GetControlBoxShadowParams {
  formVariant?: FormVariant;
  isDisabled: boolean;
  isFocused: boolean;
  isHover: boolean;
  theme: Theme;
}

const getControlBoxShadow = ({
  formVariant,
  isDisabled,
  isFocused,
  isHover,
  theme,
}: GetControlBoxShadowParams) => {
  if (isDisabled) {
    return `inset 0 0 0 1px ${theme.colors.neutral['300']}`;
  }

  if (isHover && !formVariant) {
    return isFocused
      ? `0 0 0 2px ${theme.colors.primary['300']}, inset 0 0 0 1px ${theme.colors.primary['500']}`
      : `${theme.shadows['300']}, inset 0 0 0 1px ${theme.colors.neutral['400']}`;
  }

  if (formVariant === 'success') {
    return isFocused
      ? `0 0 0 2px ${theme.colors.success['300']}, inset 0 0 0 1px ${theme.colors.success['600']}`
      : `${theme.shadows['200']}, inset 0 0 0 1px ${theme.colors.success['600']}`;
  }

  if (formVariant === 'error') {
    return isFocused
      ? `0 0 0 2px ${theme.colors.error['200']}, inset 0 0 0 1px ${theme.colors.error['500']}`
      : `${theme.shadows['200']}, inset 0 0 0 1px ${theme.colors.error['500']}`;
  }

  return isFocused
    ? `0 0 0 2px ${theme.colors.primary['300']}, inset 0 0 0 1px ${theme.colors.primary['500']}`
    : `${theme.shadows['200']}, inset 0 0 0 1px ${theme.colors.neutral['300']}`;
};

export const customTheme: (rsTheme: RSTheme, theme: Theme) => RSTheme = (rsTheme, theme) => ({
  ...rsTheme,

  spacing: {
    ...rsTheme.spacing,

    controlHeight: 36,
    baseUnit: 2,
  },

  colors: {
    ...rsTheme.colors,
    primary: theme.colors.primary['600'],
    primary75: theme.colors.primary['300'],
    primary50: theme.colors.primary['100'],
    primary25: theme.colors.primary['50'],

    danger: theme.colors.error['600'],
    dangerLight: theme.colors.error['100'],

    neutral0: theme.colors.bg,
    neutral5: theme.colors.neutral['50'],
    neutral10: theme.colors.neutral['100'],
    neutral20: theme.colors.neutral['200'],
    neutral30: theme.colors.neutral['300'],
    neutral40: theme.colors.neutral['400'],
    neutral50: theme.colors.neutral['500'],
    neutral60: theme.colors.neutral['600'],
    neutral70: theme.colors.neutral['700'],
    neutral80: theme.colors.neutral['800'],
    neutral90: theme.colors.neutral['900'],
  },
});

interface DefaultStylesParams {
  theme: Theme;
  formVariant?: FormVariant;
  noWrap?: boolean;
}

export const defaultStyles = ({
  theme,
  formVariant,
  noWrap,
}: DefaultStylesParams): Partial<ReactSelectStyles<any, any>> => ({
  input: provided => {
    return {
      ...provided,
      fontFamily: theme.fontFamily,
    };
  },

  container: provided => {
    return {
      ...provided,
      width: '100%',
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSizes.body,
    };
  },

  control: (provided, state) => {
    return {
      ...provided,
      boxShadow: getControlBoxShadow({
        isDisabled: state.isDisabled,
        isFocused: state.isFocused,
        formVariant,
        isHover: false,
        theme,
      }),
      borderWidth: 0,
      padding: '5px 0 6px 8px',
      minHeight: 36,
      backgroundColor: state.isDisabled ? theme.colors.neutral['100'] : theme.colors.bg,
      transition: 'box-shadow .15s ease-in-out',

      '&:hover': {
        boxShadow: getControlBoxShadow({
          isDisabled: state.isDisabled,
          isFocused: state.isFocused,
          formVariant,
          isHover: true,
          theme,
        }),
      },
    };
  },

  singleValue: (provided, state) => {
    return {
      ...provided,
      color: state.isDisabled ? theme.colors.neutral['500'] : theme.colors.neutral['700'],
    };
  },

  menu: provided => ({
    ...provided,
    marginTop: 4,
    border: `1px solid ${theme.colors.neutral['200']}`,
    boxShadow: theme.shadows['500'],
    borderRadius: 4,
    zIndex: 20000,
    minWidth: noWrap ? 'max-content' : 'unset',
  }),

  indicatorsContainer: provided => ({
    ...provided,
    padding: '0 8px',
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),

  placeholder: provided => ({
    ...provided,
    fontFamily: theme.fontFamily,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    color: theme.colors.neutral['700'],
  }),

  menuPortal: base => ({
    ...base,
    zIndex: 16000,
  }),

  multiValue: (base, { data: { isInvalid } }) => ({
    ...base,
    borderRadius: 4,
    backgroundColor: isInvalid ? theme.colors.error['100'] : theme.colors.neutral['200'],
  }),

  multiValueLabel: (base, { data: { isInvalid } }) => ({
    ...base,
    fontFamily: theme.fontFamily,
    borderRadius: 4,
    backgroundColor: isInvalid ? theme.colors.error['100'] : theme.colors.neutral['200'],
    color: isInvalid ? theme.colors.error['700'] : theme.colors.neutral['700'],
    fontWeight: theme.fontWeights['600'],
    fontSize: theme.fontSizes.small,
  }),

  multiValueRemove: (base, { data: { isInvalid } }) => ({
    ...base,
    borderRadius: 4,
    backgroundColor: isInvalid ? theme.colors.error['100'] : theme.colors.neutral['200'],
    color: isInvalid ? theme.colors.error['700'] : theme.colors.neutral['700'],
    paddingLeft: 3,
    paddingRight: 3,
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: isInvalid ? theme.colors.error['500'] : theme.colors.neutral['500'],
      color: theme.colors.bg,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },
  }),

  group: base => ({
    ...base,
    paddingLeft: 8,
  }),
});
